<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="true" class="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <div class="absolute inset-0 bg-black/30 backdrop-blur-sm" />
        <div class="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-fade-in">
          <!-- Header -->
          <div class="flex items-center justify-between px-6 pt-6 pb-4 shrink-0">
            <div>
              <h3 class="text-lg font-semibold text-gray-900">Upload Media</h3>
              <p class="text-sm text-gray-400 mt-0.5">Select a video file and configure transcoding</p>
            </div>
            <button @click="$emit('closed')" :disabled="uploading" class="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-50">
              <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>

          <!-- Content -->
          <div class="flex-1 overflow-y-auto px-6 pb-6">
            <!-- File selector -->
            <div class="mb-5">
              <label class="block text-sm font-medium text-gray-600 mb-1.5">Video File</label>
              <div
                @click="!uploading && $refs.fileInput.click()"
                @dragover.prevent="dragOver = true"
                @dragleave="dragOver = false"
                @drop.prevent="onFileDrop"
                :class="['border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors', dragOver ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)]' : 'border-gray-200 hover:border-gray-300', uploading ? 'opacity-50 cursor-not-allowed' : '']"
              >
                <input ref="fileInput" type="file" class="hidden" accept="video/mp4" @change="onFileChanged" :disabled="uploading" />
                <div v-if="!file">
                  <svg class="w-8 h-8 text-gray-400 mx-auto mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                  <p class="text-sm text-gray-500 font-medium">Click or drag to upload</p>
                  <p class="text-xs text-gray-400 mt-1">MP4 files only</p>
                </div>
                <div v-else class="flex items-center gap-3">
                  <div class="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center shrink-0">
                    <svg class="w-5 h-5 text-violet-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>
                  </div>
                  <div class="flex-1 min-w-0 text-left">
                    <p class="text-sm font-medium text-gray-900 truncate">{{ file.name }}</p>
                    <div class="flex items-center gap-3 mt-0.5">
                      <span class="text-xs text-gray-400">{{ $filters.formatBytes(file.size) }}</span>
                      <span v-if="fileDuration" class="inline-flex items-center gap-1 text-xs font-data text-violet-600 bg-violet-50 px-2 py-0.5 rounded">
                        <svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                        {{ formatDuration(fileDuration) }}
                      </span>
                    </div>
                  </div>
                  <button v-if="!uploading" @click.stop="clearFile" class="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                    <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
              </div>
            </div>

            <!-- Tags -->
            <div class="mb-5">
              <label class="block text-sm font-medium text-gray-600 mb-1.5">Tags</label>
              <TagSelect v-model="selectedTagIds" />
            </div>

            <!-- Categories -->
            <div class="mb-5">
              <label class="block text-sm font-medium text-gray-600 mb-1.5">Categories</label>
              <CategorySelect v-model="selectedCategoryIds" />
            </div>

            <!-- Transcoding Formats -->
            <div class="mb-5">
              <div class="flex items-center justify-between mb-1.5">
                <label class="text-sm font-medium text-gray-600">Transcoding Formats</label>
                <span v-if="fileDuration" class="text-xs font-data text-gray-400">Video duration: {{ formatDuration(fileDuration) }}</span>
              </div>
              <p v-if="!file" class="text-xs text-gray-400 mb-3">Select a file to enable format selection</p>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  v-for="fmt in formats"
                  :key="fmt.id"
                  @click="toggleFormat(fmt)"
                  :disabled="!file || fmt.disabled"
                  :class="[
                    'flex items-start gap-3 p-3 rounded-lg border text-left transition-all',
                    !file ? 'opacity-40 cursor-not-allowed border-gray-100 bg-gray-50/50' :
                    fmt.disabled ? 'opacity-40 cursor-not-allowed border-red-100 bg-red-50/30' :
                    isFormatSelected(fmt.id) ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)]' : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                  ]"
                >
                  <div :class="['w-5 h-5 rounded border flex items-center justify-center shrink-0 mt-0.5', !file || fmt.disabled ? 'border-gray-200 bg-gray-100' : isFormatSelected(fmt.id) ? 'bg-[var(--color-primary)] border-[var(--color-primary)]' : 'border-gray-300']">
                    <svg v-if="isFormatSelected(fmt.id) && file && !fmt.disabled" class="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="text-sm font-medium" :class="isFormatSelected(fmt.id) && file && !fmt.disabled ? 'text-[var(--color-primary)]' : 'text-gray-900'">{{ fmt.name }}</p>
                    <p class="text-xs text-gray-400 mt-0.5">{{ fmt.description }}</p>
                    <p v-if="fmt.maxDuration && file" :class="['text-[11px] mt-1 font-medium', fmt.disabled ? 'text-red-500' : 'text-amber-500']">
                      <svg class="w-3 h-3 inline -mt-0.5 mr-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                      Max {{ formatDuration(fmt.maxDuration / 1000) }}{{ fmt.disabled ? ' — video too long' : '' }}
                    </p>
                  </div>
                </button>
              </div>
            </div>

            <!-- Upload progress -->
            <div v-if="uploading" class="mb-4">
              <div class="flex items-center justify-between mb-2">
                <span class="text-sm font-medium text-gray-700">Uploading...</span>
                <span class="text-sm font-data text-gray-500">{{ uploadProgress }}%</span>
              </div>
              <div class="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div class="h-full bg-[var(--color-primary)] rounded-full transition-all duration-300" :style="{ width: uploadProgress + '%' }" />
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="px-6 py-4 border-t border-gray-100 flex items-center justify-between shrink-0">
            <div class="text-sm text-gray-400">
              <span v-if="selectedFormats.length">{{ selectedFormats.length }} format{{ selectedFormats.length > 1 ? 's' : '' }} selected</span>
            </div>
            <div class="flex gap-3">
              <button @click="$emit('closed')" :disabled="uploading" class="btn-secondary">Cancel</button>
              <button @click="uploadFile" :disabled="!canUpload || uploading" class="btn-primary">
                <svg v-if="!uploading" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                {{ uploading ? 'Uploading...' : 'Upload' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import TagSelect from '../../components/TagSelect.vue'
import CategorySelect from '../../components/CategorySelect.vue'
import api from '../../services/api.js'
import { notifySuccess, notifyError } from '../../services/notifications.js'

const CHUNK_SIZE = 8 * 1024 * 1024

export default {
  name: 'UploadMedia',
  components: { TagSelect, CategorySelect },
  emits: ['closed', 'created'],

  data() {
    return {
      file: null,
      fileDuration: 0,
      dragOver: false,
      uploading: false,
      uploadProgress: 0,
      selectedTagIds: [],
      selectedCategoryIds: [],
      formats: [],
      selectedFormats: ['original_abr']
    }
  },

  computed: {
    canUpload() {
      return this.file && this.selectedFormats.length > 0
    }
  },

  mounted() {
    this.loadFormats()
  },

  methods: {
    async loadFormats() {
      try {
        const res = await api.get('/formats', { silent: true })
        const formats = res.data?.formats || res.data || {}
        // Exclude original_mp4 (only for editor export queue)
        const order = ['original_abr', 'tiktok', 'instagram_reels', 'youtube_shorts', 'instagram_feed', 'twitter', 'mp3']
        this.formats = order
          .filter(id => formats[id])
          .map(id => ({ ...formats[id], id, disabled: false }))
      } catch { /* silent */ }
    },

    onFileChanged(e) {
      const f = e.target.files?.[0]
      if (!f) return
      if (f.type !== 'video/mp4') { notifyError('Only MP4 files are supported'); return }
      this.setFile(f)
    },

    onFileDrop(e) {
      this.dragOver = false
      const f = e.dataTransfer.files?.[0]
      if (!f) return
      if (f.type !== 'video/mp4') { notifyError('Only MP4 files are supported'); return }
      this.setFile(f)
    },

    setFile(f) {
      this.file = f
      this.selectedFormats = ['original_abr']
      this.extractDuration(f)
    },

    extractDuration(file) {
      const video = document.createElement('video')
      video.preload = 'metadata'
      video.onloadedmetadata = () => {
        this.fileDuration = Math.floor(video.duration)
        this.validateFormats()
        URL.revokeObjectURL(video.src)
      }
      video.src = URL.createObjectURL(file)
    },

    validateFormats() {
      const durationMs = this.fileDuration * 1000
      for (const fmt of this.formats) {
        fmt.disabled = fmt.maxDuration ? durationMs > fmt.maxDuration : false
        if (fmt.disabled) {
          this.selectedFormats = this.selectedFormats.filter(id => id !== fmt.id)
        }
      }
    },

    clearFile() {
      this.file = null
      this.fileDuration = 0
      this.selectedFormats = ['original_abr']
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
      // Reset format disabled state
      for (const fmt of this.formats) { fmt.disabled = false }
    },

    toggleFormat(fmt) {
      if (!this.file || fmt.disabled) return
      const idx = this.selectedFormats.indexOf(fmt.id)
      if (idx >= 0) this.selectedFormats.splice(idx, 1)
      else this.selectedFormats.push(fmt.id)
    },

    isFormatSelected(id) {
      return this.selectedFormats.includes(id)
    },

    formatDuration(seconds) {
      if (!seconds) return '0:00'
      const m = Math.floor(seconds / 60)
      const s = Math.floor(seconds % 60)
      return `${m}:${s.toString().padStart(2, '0')}`
    },

    async uploadFile() {
      if (!this.canUpload) return
      this.uploading = true
      this.uploadProgress = 0

      try {
        const res = await api.post('/medias', {
          filename: this.file.name,
          contentType: this.file.type,
          size: this.file.size,
          duration: this.fileDuration,
          tags: this.selectedTagIds,
          categories: this.selectedCategoryIds,
          formats: this.selectedFormats
        })

        const uploadUrl = res.data?.url || res.data?.uploadUrl
        if (!uploadUrl) throw new Error('No upload URL received')

        let offset = 0
        while (offset < this.file.size) {
          const chunk = this.file.slice(offset, offset + CHUNK_SIZE)
          const end = Math.min(offset + CHUNK_SIZE, this.file.size)

          await fetch(uploadUrl, {
            method: 'PUT',
            headers: {
              'Content-Type': this.file.type,
              'Content-Range': `bytes ${offset}-${end - 1}/${this.file.size}`
            },
            body: chunk
          })

          offset = end
          this.uploadProgress = Math.round((offset / this.file.size) * 100)
        }

        notifySuccess('Media uploaded successfully')
        this.$emit('created')
        this.$emit('closed')
      } catch (err) {
        notifyError(err?.message || 'Upload failed')
      } finally {
        this.uploading = false
      }
    }
  }
}
</script>

<style scoped>
.modal-enter-active, .modal-leave-active { transition: opacity 0.2s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
</style>
